/**
 * Shared building blocks for the versioned, scoped config screens
 * (SafetyPanel, BudgetPanel).
 *
 * Every piece here is the *exact* markup/style previously inlined in
 * SafetyPanel — both screens consume these components so the scope bar, tab
 * shell, "last modified" label, dropdown-adjacent field styling, test-panel
 * trace, and version-history drawer stay visually and behaviorally
 * identical. If a config screen needs a variation, extend these components
 * (add a prop) rather than hand-building a near-duplicate.
 */
import type { JSX, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import ResourceDrawer from '../ResourceDrawer.js'
import Select from '../../ui/Select.js'

// Field styling inside the drawers — identical to the Guardrails / Agent
// drawers (see EditAgentDrawer's inputClass/labelClass).
export const DRAWER_INPUT_CLASS =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
export const DRAWER_LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
export const DRAWER_HINT_CLASS = 'mt-1 block text-[10px] text-gray-400'
export const DRAWER_SECTION_CLASS =
  'mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500'

export function formatActor(createdBy: string): string {
  return createdBy === 'system-migration' ? 'system (migrated)' : createdBy
}

// ── LastModifiedLabel ──

export interface VersionedMeta {
  version: number
  createdBy: string
  createdAt: string
}

/** `Last modified by {user} on {date} · v{version}` */
export function LastModifiedLabel({ config }: { config: VersionedMeta }): JSX.Element {
  return (
    <span className="text-[11px] text-gray-400">
      Last modified by {formatActor(config.createdBy)} on{' '}
      {config.version === 0 ? 'never' : new Date(config.createdAt).toLocaleString()} · v
      {config.version}
    </span>
  )
}

// ── ScopeBar ──

export interface ScopeAgent {
  id: string
  slug: string
  name: string
}

export interface ScopeBarProps {
  agents: ScopeAgent[]
  scope: 'global' | 'agent'
  scopeAgentId: string | null
  onScopeChange: (scope: 'global' | 'agent', agentId: string | null) => void
  /** True when the agent scope is inheriting the global default. */
  inheriting: boolean
  /** Right-aligned content — typically a <LastModifiedLabel />. */
  trailing?: ReactNode
}

/** `Apply to: [Global (all agents) ▾]` selector + override-status badge. */
export function ScopeBar({
  agents,
  scope,
  scopeAgentId,
  onScopeChange,
  inheriting,
  trailing,
}: ScopeBarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
      <label className="text-xs font-medium text-gray-600">Apply to:</label>
      <Select
        ariaLabel="Apply to scope"
        size="sm"
        className="min-w-[220px]"
        value={scope === 'global' ? 'global' : (scopeAgentId ?? 'global')}
        onChange={(v) => {
          if (v === 'global') {
            onScopeChange('global', null)
          } else {
            onScopeChange('agent', v)
          }
        }}
      >
        <option value="global">Global (all agents)</option>
        {agents.map((a) => (
          <option key={a.id} value={a.slug}>
            {a.name} ({a.slug})
          </option>
        ))}
      </Select>
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
          scope === 'agent' && !inheriting
            ? 'bg-[#3454cf]/10 text-[#3454cf]'
            : 'bg-gray-100 text-gray-600'
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            scope === 'agent' && !inheriting ? 'bg-[#3454cf]' : 'bg-gray-400'
          }`}
        />
        {scope === 'agent' && !inheriting ? 'Override active' : 'Using: Global default'}
      </span>
      {trailing ? <span className="ml-auto">{trailing}</span> : null}
    </div>
  )
}

/** Amber banner shown when an agent scope is inheriting the global default. */
export function InheritingBanner(): JSX.Element {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
      This agent is currently using the Global default configuration. Saving changes here will
      create an agent-specific override.
    </div>
  )
}

// ── DrawerTabs ──

export interface DrawerTabItem<K extends string> {
  key: K
  label: string
  icon: ReactNode
}

/** Tab bar inside an edit drawer — same style as the tool/agent drawers. */
export function DrawerTabs<K extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: readonly DrawerTabItem<K>[]
  active: K
  onChange: (key: K) => void
}): JSX.Element {
  return (
    <div className="-mx-6 -mt-6 mb-5 flex border-b border-gray-200 px-4">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
            active === tab.key
              ? 'border-[#3454cf] text-[#3454cf]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// ── TestPanel ──

export interface TestTraceStep {
  rule: string
  passed: boolean
  detail: string
}

export interface TestTraceResult {
  steps: TestTraceStep[]
  blocked: boolean
  result: string
  fallbackMessage?: string | null
}

/** Pass/fail trace block rendered after a dry-run — shared visual style. */
export function TestTrace({ result }: { result: TestTraceResult }): JSX.Element {
  return (
    <div className="mt-2 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-700">
      {result.steps.map((step, i) => (
        <div key={i} className={step.passed ? 'text-gray-700' : 'text-red-600'}>
          {step.passed ? '✓' : '✗'} {step.rule}: {step.detail}
        </div>
      ))}
      <div
        className={`mt-2 font-semibold ${result.blocked ? 'text-red-700' : 'text-gray-900'}`}
      >
        Result: {result.result}
      </div>
      {result.blocked && result.fallbackMessage ? (
        <div className="mt-1 text-gray-600">
          Fallback shown: &quot;{result.fallbackMessage}&quot;
        </div>
      ) : null}
    </div>
  )
}

export interface TestPanelProps {
  /** Section title, e.g. "Test this configuration". */
  title: ReactNode
  /** Input area rendered before the Run button — the screen's test fields. */
  children: ReactNode
  onRun: () => void
  running: boolean
  runDisabled: boolean
  result: TestTraceResult | null
}

/**
 * Dry-run test panel shell: title + input row + "Run Test" button + the
 * "tests your current unsaved changes" note + pass/fail trace. Check
 * functions differ per screen; the shell is shared.
 */
export function TestPanel({
  title,
  children,
  onRun,
  running,
  runDisabled,
  result,
}: TestPanelProps): JSX.Element {
  return (
    <div>
      <h4 className={`${DRAWER_SECTION_CLASS} flex items-center gap-1.5`}>{title}</h4>
      <div className="flex gap-2">
        {children}
        <button
          type="button"
          onClick={onRun}
          disabled={running || runDisabled}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Run Test
        </button>
      </div>
      <span className={DRAWER_HINT_CLASS}>
        Tests your current unsaved changes, not the last saved version.
      </span>
      {result ? <TestTrace result={result} /> : null}
    </div>
  )
}

// ── VersionHistoryDrawer ──

export interface VersionRow extends VersionedMeta {
  id: string
  isActive: boolean
}

export interface VersionHistoryDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  versions: VersionRow[]
  onRestore: (versionId: string) => void
}

/** Version-rollback side panel — identical pattern across config screens. */
export function VersionHistoryDrawer({
  open,
  onClose,
  title,
  versions,
  onRestore,
}: VersionHistoryDrawerProps): JSX.Element {
  return (
    <ResourceDrawer open={open} onClose={onClose} title={title} width="standard">
      <div className="divide-y divide-gray-100">
        {versions.length === 0 && (
          <p className="py-6 text-xs text-gray-400">No saved versions for this scope.</p>
        )}
        {versions.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-3 py-3">
            <div>
              <p className="text-xs font-medium text-gray-900">
                v{v.version}
                {v.isActive && (
                  <span className="ml-2 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                    active
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {new Date(v.createdAt).toLocaleString()} · by {formatActor(v.createdBy)}
              </p>
            </div>
            {!v.isActive && (
              <button
                type="button"
                onClick={() => onRestore(v.id)}
                className="rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
              >
                Restore this version
              </button>
            )}
          </div>
        ))}
      </div>
    </ResourceDrawer>
  )
}
